/**
 * AUTH-002 §6.1 item 6 — a 429 on token refresh backs off and never loops.
 *
 * auth-js treats a 429 as a non-retryable error: it deletes the session and emits
 * SIGNED_OUT, so a rate limit (which is per IP, shared by the whole clinic network)
 * would sign the user out. The Supabase fetch wrapper therefore turns a 429 on
 * `grant_type=refresh_token` into a thrown network error (auth-js keeps the session and
 * reports a retryable failure) and opens this gate. While the gate is open, refresh
 * requests fail locally without touching the network. Back-off doubles per 429.
 */
import { REFRESH_BACKOFF_INITIAL_MS, REFRESH_BACKOFF_MAX_MS } from './session-policy';

export interface RefreshGate {
  /** True while refresh requests must not go on the wire. */
  isOpen(): boolean;
  /** ms until the gate closes, 0 when closed. */
  remainingMs(): number;
  /** Record a 429. */
  trip(): void;
  /** Record a successful refresh. */
  reset(): void;
}

export function createRefreshGate(now: () => number): RefreshGate {
  let openUntil = 0;
  let backoff = 0;
  return {
    isOpen: () => now() < openUntil,
    remainingMs: () => Math.max(0, openUntil - now()),
    trip() {
      backoff = backoff
        ? Math.min(backoff * 2, REFRESH_BACKOFF_MAX_MS)
        : REFRESH_BACKOFF_INITIAL_MS;
      openUntil = now() + backoff;
    },
    reset() {
      openUntil = 0;
      backoff = 0;
    },
  };
}
